import { useContext, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Link,
} from '@mui/material';
import { AppContext } from '../../context/AppContext';
import FileUpload from '../Common/FileUpload';
import ProductPriceEditSection from '../Common/ProductPriceEditSection';
import { PRODUCT_TYPES } from '../../utils/constants';
import { validateRequired } from '../../utils/validation';

const PRODUCT_DESCRIPTION_MAX_LENGTH = 250;

const productTextFieldSx = {
  '& .MuiInputBase-input': {
    color: '#0F1214',
    fontFamily: 'Inter',
  },
  '& .MuiInputBase-input::placeholder': {
    color: '#0F1214',
    fontFamily: 'Inter',
    opacity: 1,
  },
  '& .MuiInputLabel-root': {
    color: '#0F1214',
    fontFamily: 'Inter',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#0F1214',
  },
};

const CheckoutEditPane = () => {
  const { state, updateProductType, updateProduct } = useContext(AppContext);
  const [errors, setErrors] = useState({});

  const handleProductTypeChange = (event) => {
    updateProductType(event.target.value);
    // Clear digital file when switching to standard
    if (event.target.value === PRODUCT_TYPES.STANDARD) {
      updateProduct({
        digitalFileUrl: '',
        internalFilename: '',
        digitalFileContentType: '',
        digitalFileName: '',
        digitalFileContentLength: 0,
        digitalFilePreviewDataUrl: '',
      });
    }
  };

  const handleFieldChange = (field, value) => {
    // Clear error for this field
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
    const nextValue =
      field === 'description' ? value.slice(0, PRODUCT_DESCRIPTION_MAX_LENGTH) : value;
    updateProduct({ [field]: nextValue });
  };

  const handleValidation = (field, value) => {
    let error = '';
    if (field === 'name') {
      error = validateRequired(value, 'Product name');
    } else if (field === 'description') {
      error = validateRequired(value, 'Product description');
    }
    setErrors({ ...errors, [field]: error });
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, width: '100%' }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: '#23272E' }}>
        Your checkout page
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontWeight: 400, color: '#5C5C5C', fontSize: '14px', lineHeight: '24px' }}>
        Using the power of AI, we've prebuilt your checkout page - just add your product details below.
      </Typography>

      <FormControl component="fieldset" sx={{ mb: 3 }}>
        <RadioGroup
          row
          value={state.productType}
          onChange={handleProductTypeChange}
          sx={{
            flexWrap: 'nowrap',
            gap: { xs: 1, sm: 2 },
            width: '100%',
            '& .MuiFormControlLabel-root': {
              marginRight: 0,
              flex: 1,
            },
            '& .MuiFormControlLabel-label': {
              whiteSpace: 'nowrap',
            },
          }}
        >
          <FormControlLabel
            value={PRODUCT_TYPES.STANDARD}
            control={<Radio sx={{ color: '#326EEA', '&.Mui-checked': { color: '#326EEA' } }} />}
            label="Standard Product"
          />
          <FormControlLabel
            value={PRODUCT_TYPES.DIGITAL}
            control={<Radio sx={{ color: '#326EEA', '&.Mui-checked': { color: '#326EEA' } }} />}
            label="Digital Product"
          />
        </RadioGroup>
      </FormControl>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <TextField
          label="Product name"
          value={state.product.name}
          onChange={(e) => handleFieldChange('name', e.target.value)}
          onBlur={(e) => handleValidation('name', e.target.value)}
          error={!!errors.name}
          helperText={errors.name}
          fullWidth
          required
          sx={productTextFieldSx}
        />

        <TextField
          label="Product description"
          value={state.product.description}
          onChange={(e) => handleFieldChange('description', e.target.value)}
          onBlur={(e) => handleValidation('description', e.target.value)}
          error={!!errors.description}
          helperText={
            errors.description ||
            `${(state.product.description || '').length}/${PRODUCT_DESCRIPTION_MAX_LENGTH}`
          }
          fullWidth
          multiline
          rows={4}
          required
          inputProps={{ maxLength: PRODUCT_DESCRIPTION_MAX_LENGTH }}
          sx={productTextFieldSx}
        />

        <ProductPriceEditSection />

        <FileUpload
          label="Upload product image"
          value={state.product.imageUrl}
          onChange={(url) => handleFieldChange('imageUrl', url)}
          type="image"
        />

        {state.productType === PRODUCT_TYPES.DIGITAL && (
          <>
            <FileUpload
              label="Upload your digital file"
              value={state.product.digitalFileUrl}
              digitalFilePreviewDataUrl={state.product.digitalFilePreviewDataUrl}
              onChange={(url) => handleFieldChange('digitalFileUrl', url)}
              onInternalFilenameChange={(name) =>
                handleFieldChange('internalFilename', name)
              }
              onFileMetadataChange={({ contentType, fileName, contentLength, imagePreviewDataUrl }) =>
                updateProduct({
                  digitalFileContentType: contentType,
                  digitalFileName: fileName,
                  digitalFileContentLength: contentLength,
                  digitalFilePreviewDataUrl: imagePreviewDataUrl || '',
                })
              }
              type="digital"
            />
            <Typography variant="caption" color="text.secondary">
              The file content must adhere to our{' '}
              <Link
                href="https://legal.newfold.com/TermsOfUse.pdf"
                underline="hover"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </Link>
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

export default CheckoutEditPane;
